import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';

import PlayerTable from '../../../Common/PlayerTable';
import { DeleteModal } from '../../../Common/DeleteModal';
import Select from 'react-select';
import { 
  getPlayersByClubId,
  deletePlayer
} from '../../../api/club/playerService';
import { fetchTeamsByClubId, getAgeCategoriesFromTournaments } from '../../../api/club/teamService';
import { getUserClubIds } from '../../../utils/roleBasedAccess';
import './PlayerInformation.css';
import '../../../components/PlayerComponents.css';
import { getImageUrl } from '../../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../../components/PlayerAssetPreviewModal';
import { PLAYER_ASSETS_ENABLED } from '../../../constants/featureFlags';
import { downloadFileFromUrl } from '../../../utils/fileDownload';



interface Player {
  _id: string;
  playerId?: string;
  fullName: string;
  team: {
    name: string;
    shortName: string;
    logo: string;
  };
  dob: string;
  sports: string;
  verifyStat: 'Approved' | 'Referred Back' | 'Pending';
  paymentStat: 'Paid' | 'Not Paid' | 'Pending';
  amountPaid: number;
  amountDue: number;
  playerImage: string;
  ageCategory: string;
  qrCodeUrl?: string;
  playerIdCardUrl?: string;
}

interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
}

const PlayerInformation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageCategoryFilter, setAgeCategoryFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [assetPreviewConfig, setAssetPreviewConfig] = useState<{ player: Player; type: PlayerAssetType } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Dynamic filter options
  const [dynamicAgeCategoryOptions, setDynamicAgeCategoryOptions] = useState<Array<{value: string, label: string}>>([]);
  const [dynamicTeamOptions, setDynamicTeamOptions] = useState<Array<{value: string, label: string}>>([]);





  // Age category options - use dynamic data if available, otherwise fallback to static
  const ageCategoryOptions = dynamicAgeCategoryOptions.length > 0 ? dynamicAgeCategoryOptions : [
    { value: 'all', label: 'All' },
    { value: 'U7', label: 'U7' },
    { value: 'U10', label: 'U10' },
    { value: 'U11', label: 'U11' },
    { value: 'U12', label: 'U12' },
    { value: 'U13', label: 'U13' },
    { value: 'U14', label: 'U14' },
    { value: 'U15', label: 'U15' },
    { value: 'U16', label: 'U16' },
    { value: 'U17', label: 'U17' },
    { value: 'U18', label: 'U18' }
  ];

  // Team options - use dynamic data if available, otherwise fallback to static
  const teamOptions = dynamicTeamOptions.length > 0 ? dynamicTeamOptions : [
    { value: 'all', label: 'All' },
    { value: 'OFC', label: 'Oranje FC' },
    { value: 'PFC', label: 'Pneuma FC' },
    { value: 'CFC', label: 'Community FC' }
  ];

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Referred Back', label: 'Referred Back' },
    { value: 'Pending', label: 'Pending' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Refresh data when component comes back into focus (e.g., after navigation)
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 Player Information page focused, refreshing data...');
      loadData();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Refresh data when location changes (navigation back to this page)
  useEffect(() => {
    console.log('🔄 Location changed, refreshing player data...');
    loadData();
  }, [location.pathname]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load teams and age categories
      await Promise.all([
        loadTeams(),
        loadAgeCategories()
      ]);
      
      // Load players from all teams (page 1)
      await loadAllPlayers(1);
      
    } catch (error) {
      console.error('Error loading data:', error);
      // Don't fallback to mock data - let the table show empty state
      setPlayers([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const loadAllPlayers = async (page: number = 1) => {
    try {
      setIsPageLoading(true);
      
      // Get club IDs for the current user
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      console.log('User club IDs:', clubIds);
      
      if (clubIds.length === 0) {
        console.log('No club IDs found for user, showing empty state');
        setPlayers([]);
        setTotalCount(0);
        setTotalPages(0);
        return;
      }
      
      // Use the first club ID (or you can implement logic to handle multiple clubs)
      const clubId = clubIds[0];
      console.log('Loading players for club ID:', clubId, 'Page:', page);
      
      try {
        const result = await getPlayersByClubId(clubId, page);
        console.log('Raw players data from club API:', result);
        
        if (result.players && result.players.length > 0) {
          // Convert API players to legacy Player format for compatibility
          const convertedPlayers: Player[] = result.players.map(apiPlayer => {
            // Get payment amounts with fallbacks to 0
            const amountPaid = apiPlayer.amountPaid ?? 0;
            const amountDue = apiPlayer.amountDue ?? 0;
            
            // Determine payment status based on amountPaid and amountDue
            let paymentStat: 'Paid' | 'Not Paid' | 'Pending' = 'Not Paid';
            if (amountPaid > 0 && amountDue === 0) {
              paymentStat = 'Paid';
            } else if (amountPaid > 0 && amountDue > 0) {
              paymentStat = 'Pending';
            }
            
            return {
              _id: apiPlayer._id,
              playerId: apiPlayer.playerId,
              fullName: apiPlayer.name,
              team: {
                name: apiPlayer.teamInfo?.name || 'Unknown Team',
                shortName: apiPlayer.teamInfo?.name?.substring(0, 3).toUpperCase() || 'UT',
                logo: apiPlayer.teamInfo?.logo || ''
              },
              dob: apiPlayer.dob,
              sports: 'Football', // Default sport - you might want to get this from player data if available
              verifyStat: apiPlayer.verificationStatus === 'approved' ? 'Approved' : 
                         apiPlayer.verificationStatus === 'referred_back' ? 'Referred Back' : 'Pending',
              paymentStat: paymentStat,
              amountPaid: amountPaid,
              amountDue: amountDue,
              playerImage: apiPlayer.profilePic ? getImageUrl(apiPlayer.profilePic) : '',
              ageCategory: apiPlayer.ageCategory,
              qrCodeUrl: apiPlayer.qrCode ? getImageUrl(apiPlayer.qrCode) : '',
              playerIdCardUrl: apiPlayer.playerIdCard ? getImageUrl(apiPlayer.playerIdCard) : ''
            };
          });
          
          setPlayers(convertedPlayers);
          setTotalCount(result.totalCount);
          setTotalPages(Math.ceil(result.totalCount / 10));
          console.log('Converted and set players:', convertedPlayers);
          console.log('Total count:', result.totalCount, 'Total pages:', Math.ceil(result.totalCount / 10));
        } else {
          console.log('No players found from club API, showing empty state');
          setPlayers([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } catch (apiError) {
        console.error('Failed to load players from club API:', apiError);
        setPlayers([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Failed to load all players:', error);
      setPlayers([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsPageLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      // Get club IDs for the current user
      const userData = {
        ...user,
        subRole: role,
        permissions: permissions
      };
      const clubIds = getUserClubIds(userData);
      
      if (clubIds.length > 0) {
        try {
          // Use the first club ID (assuming single club access for now)
          const clubId = clubIds[0];
          
          // Fetch teams from API with dynamic club ID
          const teamsData = await fetchTeamsByClubId(clubId);
          console.log('Raw teams data from new API:', teamsData);
          
          // Transform TeamData to Team interface
          const transformedTeams: Team[] = teamsData.map((team: any) => ({
            _id: team._id,
            name: team.name,
            shortName: team.shortName,
            logo: team.logo,
            ageGroups: [team.ageCategory], // Convert single ageCategory to array
            coachManagerName: '', // Not provided by API
            contactDetails: '' // Not provided by API
          }));
          setTeams(transformedTeams);
          
          // Update dynamic team options for filter
          const teamFilterOptions = [
            { value: 'all', label: 'All' },
            ...transformedTeams.map(team => ({
              value: team.name,
              label: team.name
            }))
          ];
          setDynamicTeamOptions(teamFilterOptions);
          
          console.log('Transformed teams:', transformedTeams);
          console.log('Team filter options:', teamFilterOptions);
        } catch (apiError) {
          console.error('Failed to load teams from API:', apiError);
          // Set default teams if API fails
          const defaultTeams = [
            {
              _id: '1',
              name: 'Oranje FC',
              shortName: 'OFC',
              logo: '',
              ageGroups: ['U10', 'U12', 'U14'],
              coachManagerName: 'John Doe',
              contactDetails: '+91 9876543210'
            }
          ];
          setTeams(defaultTeams);
          
          // Set default team filter options
          setDynamicTeamOptions([
            { value: 'all', label: 'All' },
            { value: 'OFC', label: 'Oranje FC' }
          ]);
        }
      } else {
        console.error('No club access found for this user');
        // Set default teams if no club access
        const defaultTeams = [
          {
            _id: '1',
            name: 'Oranje FC',
            shortName: 'OFC',
            logo: '',
            ageGroups: ['U10', 'U12', 'U14'],
            coachManagerName: 'John Doe',
            contactDetails: '+91 9876543210'
          }
        ];
        setTeams(defaultTeams);
        
        // Set default team filter options
        setDynamicTeamOptions([
          { value: 'all', label: 'All' },
          { value: 'OFC', label: 'Oranje FC' }
        ]);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const loadAgeCategories = async () => {
    try {
      const categories = await getAgeCategoriesFromTournaments();
      setAgeCategories(categories);
      
      // Update dynamic age category options for filter
      const ageCategoryFilterOptions = [
        { value: 'all', label: 'All' },
        ...categories.map(category => ({
          value: category,
          label: category
        }))
      ];
      setDynamicAgeCategoryOptions(ageCategoryFilterOptions);
      
      console.log('Age categories loaded:', categories);
      console.log('Age category filter options:', ageCategoryFilterOptions);
    } catch (error) {
      console.error('Failed to load age categories:', error);
      // Set default categories if API fails
      const defaultCategories = ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];
      setAgeCategories(defaultCategories);
      
      // Set default age category filter options
      const defaultAgeCategoryOptions = [
        { value: 'all', label: 'All' },
        ...defaultCategories.map(category => ({
          value: category,
          label: category
        }))
      ];
      setDynamicAgeCategoryOptions(defaultAgeCategoryOptions);
    }
  };

  // Filter players based on search term and filters
  const filteredPlayers = players.filter(player => {
    const matchesSearch = searchTerm === '' || 
      player.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAgeCategory = ageCategoryFilter === 'all' || 
      player.ageCategory === ageCategoryFilter;
    
    const matchesTeam = teamFilter === 'all' || 
      player.team.name === teamFilter;
    
    const matchesStatus = statusFilter === 'all' || 
      player.verifyStat === statusFilter;
    
    return matchesSearch && matchesAgeCategory && matchesTeam && matchesStatus;
  });

  // Event handlers

  const handleAddPlayer = () => {
    if (teams.length === 0) {
      alert('No teams available. Please create a team first.');
      return;
    }
    
    // Navigate to player detail page for adding new player
    // Use the first available team or navigate to team selection
    const teamId = teams[0]?._id || '';
    navigate(`/my-club/player-information/add?teamId=${teamId}`);
  };

  const handleEditPlayer = (player: Player) => {
    // Navigate to player detail page for editing
    // Find the team for this player or use the first available team
    const teamId = teams.find(team => team.shortName === player.team.shortName)?._id || teams[0]?._id || '';
    navigate(`/my-club/player-information/${player._id}?teamId=${teamId}&edit=true`);
  };

  const handleViewPlayer = (player: Player) => {
    // Navigate to player detail page for viewing
    // Find the team for this player or use the first available team
    const teamId = teams.find(team => team.shortName === player.team.shortName)?._id || teams[0]?._id || '';
    navigate(`/my-club/player-information/${player._id}?teamId=${teamId}`);
  };

  const handleAssetView = (player: Player, assetType: PlayerAssetType) => {
    setAssetPreviewConfig({ player, type: assetType });
  };

  const handleAssetDownload = (player: Player, assetType: PlayerAssetType) => {
    const assetUrl = assetType === 'qrCode' ? player.qrCodeUrl : player.playerIdCardUrl;
    if (!assetUrl) return;

    const fallbackName = player.fullName?.replace(/\s+/g, '-').toLowerCase();
    const filename =
      assetType === 'qrCode'
        ? `${player.playerId || fallbackName || 'player'}-qr-code.png`
        : `${player.playerId || fallbackName || 'player'}-id-card.pdf`;

    downloadFileFromUrl(assetUrl, filename);
  };

  const handleDeletePlayer = async (player: Player) => {
    setPlayerToDelete(player);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (playerToDelete) {
      try {
        setIsDeleting(true);
        setDeleteError(null);
        
        await deletePlayer(playerToDelete._id);
        
        // Success - close modal and refresh data
        setShowDeleteModal(false);
        setPlayerToDelete(null);
        setDeleteError(null);
        
        // Refresh players data instead of reloading page
        await loadAllPlayers();
      } catch (error: any) {
        console.error('Failed to delete player:', error);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete player. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setPlayerToDelete(null);
  };

  const handlePageChange = async (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    
    setCurrentPage(page);
    
    // Scroll to top for better UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Load players for the new page
    await loadAllPlayers(page);
  };

  const handleGoBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="player-information-page">
        <Container fluid style={{ padding: '0 15px', marginTop: '20px' }}>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3">
            <div className="shimmer-text" style={{ width: '300px', height: '24px' }}></div>
          </div>
          
          {/* Header Shimmer */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div className="shimmer-circle" style={{ width: '48px', height: '48px', marginRight: '16px' }}></div>
              <div className="shimmer-text" style={{ width: '200px', height: '32px' }}></div>
            </div>
            <div className="shimmer-text" style={{ width: '400px', height: '16px' }}></div>
          </div>
          
          {/* Filters Shimmer */}
          <div className="mb-4">
            <div className="d-flex gap-3">
              <div className="shimmer-text" style={{ width: '140px', height: '40px' }}></div>
              <div className="shimmer-text" style={{ width: '140px', height: '40px' }}></div>
              <div className="shimmer-text" style={{ width: '140px', height: '40px' }}></div>
            </div>
          </div>
          
          {/* Table Shimmer */}
          <div className="shimmer-container">
            <div className="shimmer-row" style={{ height: '60px', marginBottom: '10px' }}></div>
            <div className="shimmer-row" style={{ height: '60px', marginBottom: '10px' }}></div>
            <div className="shimmer-row" style={{ height: '60px', marginBottom: '10px' }}></div>
            <div className="shimmer-row" style={{ height: '60px', marginBottom: '10px' }}></div>
            <div className="shimmer-row" style={{ height: '60px' }}></div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="player-information-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'My Club' },
              { label: 'Player Information', active: true }
            ]}
          />
        </div>

        {/* Page Header - Simplified for Player Information */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex justify-content-between align-items-start">
            {/* Left: Players Title and Description */}
            <div className="players-section-info">
              <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>
                Players ({totalCount})
              </h4>
              <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>
                Manage your club players and view player details
              </p>
            </div>
            
            {/* Right: Add Button */}
            <div className="filter-controls-right">
              <Button
                variant="primary"
                onClick={handleAddPlayer}
                className="add-player-btn"
                style={{
                  backgroundColor: '#0d6efd',
                  borderColor: '#0d6efd',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Add New Player
                <i className="ri-add-line ms-1"></i>
              </Button>
            </div>
          </div>
        </div>





        {/* Player Listing Table */}
        <Card className="players-card mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">Player Listing</h5>
              <div className="d-flex align-items-center gap-3">
                <div className="filter-group">
                  <label className="form-label mb-1">Age Category</label>
                  <Select
                    classNamePrefix="react-select"
                    value={ageCategoryOptions.find(option => option.value === ageCategoryFilter)}
                    onChange={(selectedOption) => setAgeCategoryFilter(selectedOption?.value || 'all')}
                    options={ageCategoryOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '140px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                            </div>
                <div className="filter-group">
                  <label className="form-label mb-1">Team</label>
                  <Select
                    classNamePrefix="react-select"
                    value={teamOptions.find(option => option.value === teamFilter)}
                    onChange={(selectedOption) => setTeamFilter(selectedOption?.value || 'all')}
                    options={teamOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '140px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                            </div>
                <div className="filter-group">
                  <label className="form-label mb-1">Status</label>
                  <Select
                    classNamePrefix="react-select"
                    value={statusOptions.find(option => option.value === statusFilter)}
                    onChange={(selectedOption) => setStatusFilter(selectedOption?.value || 'all')}
                    options={statusOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '140px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                          </div>
              </div>
            </div>



            <PlayerTable
              players={filteredPlayers}
              loading={loading || isPageLoading}
              showView={true}
              showEdit={true}
              showDelete={true}
              showPaymentColumns={false}
              showQrActions={PLAYER_ASSETS_ENABLED}
              showPlayerIdCardActions={PLAYER_ASSETS_ENABLED}
              assetColumnMode="separate"
              onAssetView={handleAssetView}
              onAssetDownload={handleAssetDownload}
              onView={handleViewPlayer}
              onEdit={handleEditPlayer}
              onDelete={handleDeletePlayer}
              title="Player Lineups"
              emptyMessage="No players found"
              emptySubMessage="No players available. Add a new player to get started."
            />

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-section mt-4" style={{ 
                padding: '16px 0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Page Info */}
                <div className="page-info mb-3">
                  <span className="text-muted" style={{ 
                    fontSize: '14px', 
                    fontWeight: '500',
                    color: '#6c757d'
                  }}>
                    Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} players
                  </span>
                </div>

                {/* Pagination Controls */}
                <div className="pagination-controls d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                  {/* Previous Button */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isPageLoading}
                    style={{ 
                      minWidth: '90px', 
                      height: '36px',
                      fontSize: '13px',
                      borderColor: currentPage === 1 ? '#e9ecef' : '#ced4da',
                      backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                      color: currentPage === 1 ? '#adb5bd' : '#495057',
                      borderRadius: '6px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <i className="ri-arrow-left-s-line me-1"></i>
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <div className="d-flex" style={{ gap: '4px' }}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'primary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        disabled={isPageLoading}
                        style={{ 
                          minWidth: '36px', 
                          height: '36px',
                          fontSize: '13px',
                          fontWeight: currentPage === pageNum ? '600' : '500',
                          borderColor: currentPage === pageNum ? '#0d6efd' : '#ced4da',
                          backgroundColor: currentPage === pageNum ? '#0d6efd' : '#ffffff',
                          color: currentPage === pageNum ? '#ffffff' : '#495057',
                          borderRadius: '6px',
                          boxShadow: currentPage === pageNum ? '0 2px 4px rgba(13, 110, 253, 0.15)' : 'none',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {pageNum}
                      </Button>
                    ))}
                  </div>

                  {/* Next Button */}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || isPageLoading}
                    style={{ 
                      minWidth: '90px', 
                      height: '36px',
                      fontSize: '13px',
                      borderColor: currentPage === totalPages ? '#e9ecef' : '#ced4da',
                      backgroundColor: currentPage === totalPages ? '#f8f9fa' : '#ffffff',
                      color: currentPage === totalPages ? '#adb5bd' : '#495057',
                      borderRadius: '6px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Next
                    <i className="ri-arrow-right-s-line ms-1"></i>
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>


      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCancelDelete}
        deleteModalFunction={handleConfirmDelete}
        title="Delete Player"
        message={deleteError || `Are you sure you want to delete "${playerToDelete?.fullName}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={!!assetPreviewConfig}
          assetType={assetPreviewConfig?.type || 'qrCode'}
          assetUrl={
            assetPreviewConfig
              ? assetPreviewConfig.type === 'qrCode'
                ? assetPreviewConfig.player.qrCodeUrl
                : assetPreviewConfig.player.playerIdCardUrl
              : undefined
          }
          playerName={assetPreviewConfig?.player.fullName}
          onClose={() => setAssetPreviewConfig(null)}
          onDownload={() => {
            if (!assetPreviewConfig) return;
            handleAssetDownload(assetPreviewConfig.player, assetPreviewConfig.type);
          }}
        />
      )}
    </div>
  );
};

export default PlayerInformation; 